import { useState } from "react";
import { connect } from "react-redux";
import { ProviderContext, withSnackbar } from "notistack";
import FirebaseService from "../../services/firebase/FirebaseService";
import { RootState } from "../../state/configureStore";
import StandardModal from "./StandardModal";
import ModalSize from "./ModalSize";
import LoadingButton from "../Loading/LoadingButton";
import NewClassView from "../Screens/Teacher/NewClassView/NewClassView";
import { useHistory } from "react-router-dom";
import { ReadleeGoogleCourse } from "../../interfaces/Google";

// Modal for creating new class via Google Classroom or title input

interface ModalProps {
  open: boolean;
  cancelClose: () => void;
  size: ModalSize;
}

function NewClassModal(props: ModalProps & ProviderContext & ReturnType<typeof mapStateToProps>) {
  // react-router hook for redirect
  const history = useHistory();

  const [className, setClassName] = useState("");
  const [googleClasses, setGoogleClasses] = useState<ReadleeGoogleCourse[]>([]);
  const [classesToBeMade, setClassesToBeMade] = useState<ReadleeGoogleCourse[]>([]);

  // Local loading button state and functionality to be passed to Standard Modal
  const [loadingButtonState, setLoadingButtonState] = useState(LoadingButton.BeforeLoading);

  const close = () => {
    props.cancelClose();
    setLoadingButtonState(LoadingButton.BeforeLoading);
    setClassesToBeMade([]);
    setGoogleClasses([]);
    setClassName("");
  };

  const createClasses = async () => {
    setLoadingButtonState(LoadingButton.Loading);
    // variable to store the new class id, to be used in redirect after creation
    let newClassId: string | undefined;
    // Create google classes if selected
    if (classesToBeMade.length > 0) {
      for (const [i, currentClass] of classesToBeMade.entries()) {
        // store google classroom's student emails
        const classStudents = currentClass.students.map(student => student.profile.emailAddress);

        // create class with students and store ID
        const classId = await FirebaseService.createClass(
          currentClass.name,
          props.userName,
          props.userEmail,
          props.userId,
          currentClass.id,
          classStudents,
        );
        // if the first class in the array, store set the id as the newClassId
        if (i === 0) newClassId = classId;
      }
      props.enqueueSnackbar("Classes successfully made", { variant: "success", autoHideDuration: 5000 });

      // Else create class based on text input
    } else if (className.length > 0) {
      // set returned id to newClassId
      newClassId = await FirebaseService.createClass(
        className,
        props.userName,
        props.userEmail,
        props.userId,
        null,
        [],
      );
      props.enqueueSnackbar("Class successfully made", { variant: "success", autoHideDuration: 5000 });
    }
    setLoadingButtonState(LoadingButton.DoneLoading);
    // close modal
    close();
    // redirect to new class (or first class in list)
    if (newClassId !== undefined) history.push(`/Class/${newClassId}`);
  };

  return (
    <StandardModal
      cancelClose={close}
      title="Create A New Class"
      open={props.open}
      size={props.size}
      onlyOneButton={false}
      onSubmit={createClasses}
      loadingButtonState={loadingButtonState}
      changeLoadingButtonState={setLoadingButtonState}
    >
      <NewClassView
        setClassesToBeMade={setClassesToBeMade}
        setClassName={setClassName}
        setGoogleClasses={setGoogleClasses}
        className={className}
        classesToBeMade={classesToBeMade}
        googleClasses={googleClasses}
      />
    </StandardModal>
  );
}

const mapStateToProps = (state: RootState) => {
  return {
    userId: state.currentUserState.currentUser?.uid || "",
    userName: state.currentUserState.currentUser?.displayName || "",
    userEmail: state.currentUserState.currentUser?.email || "",
  };
};

export default connect(mapStateToProps)(withSnackbar(NewClassModal));
